import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      borderRadius: ['btn'],
    },
    classGroups: {
      'font-size': [
        'text-heading-1',
        'text-heading-2',
        'text-heading-3',
        'text-heading-4',
        'text-heading-5',
        'text-text-3',
        'text-text-2',
        'text-text-1',
      ],
      'text-color': [
        'text-info-primary',
        'text-info-secondary',
        'text-info-tertiary',
        'text-info-quaternary',
        'text-info-white',
      ],
      leading: ['leading-tight', 'leading-normal', 'leading-loose'],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function noop(): any {}

export function atobID(id: string) {
  if (id && typeof id === 'string' && id.includes('/')) {
    return id.split('/').pop()?.split('?')?.shift()
  } else {
    return id
  }
}
export const spaceToHyphen = (str: string) => str?.replace(/\s+/g, '-')

/**
 * 转义正则表达式特殊字符
 * @param str 需要转义的字符串
 * @returns 转义后的字符串
 */
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * 高亮搜索关键词
 * @param text 原始文本
 * @param searchText 搜索关键词
 * @returns 高亮后的 HTML 字符串
 */
export const highlightSearchWord = (text: string, searchText: string) => {
  try {
    if (!text || !searchText) {
      return text
    }

    // 转义搜索文本中的正则表达式特殊字符
    const escapedSearchText = escapeRegExp(searchText)
    const regex = new RegExp(escapedSearchText, 'gi')

    // 使用正则表达式的匹配结果来保持原始大小写
    return text.replace(regex, match => `<span class='text-brand-0 font-bold'>${match}</span>`)
  } catch (e) {
    console.error('highlightSearchWord error:', e)
    return text
  }
}

/**
 * 获取本地化路径
 *
 * 根据语言标识为路径添加对应的 locale 前缀。
 * - 'us' 为默认语言，不添加前缀
 * - 其他语言会添加 `/{locale}` 前缀
 * - 完整 URL（http/https）不做处理
 * - 自动处理路径格式（补充前导斜杠、移除尾部斜杠）
 *
 * @param path - 原始路径
 * @param locale - 语言标识（如 'us', 'uk', 'de', 'jp' 等）
 * @returns 本地化后的路径
 *
 * @example
 * // 基本用法 - 默认语言 'us' 不添加前缀
 * getLocalizedPath('/products', 'us')          // => '/products'
 * getLocalizedPath('/about', 'us')             // => '/about'
 *
 * @example
 * // 其他语言添加前缀
 * getLocalizedPath('/products', 'uk')          // => '/uk/products'
 * getLocalizedPath('/about/team', 'de')        // => '/de/about/team'
 * getLocalizedPath('/contact', 'jp')           // => '/jp/contact'
 *
 * @example
 * // 根路径处理
 * getLocalizedPath('/', 'us')                  // => '/'
 * getLocalizedPath('/', 'uk')                  // => '/uk'
 *
 * @example
 * // 自动格式化路径
 * getLocalizedPath('products', 'uk')           // => '/uk/products' (补充前导斜杠)
 * getLocalizedPath('/products/', 'uk')         // => '/uk/products' (移除尾部斜杠)
 * getLocalizedPath('//products//detail/', 'uk') // => '/uk/products/detail' (移除多余斜杠)
 *
 * @example
 * // 已包含 locale 的路径不重复添加
 * getLocalizedPath('/uk/products', 'uk')       // => '/uk/products'
 *
 * @example
 * // 完整 URL 不处理
 * getLocalizedPath('https://example.com/page', 'uk')  // => 'https://example.com/page'
 * getLocalizedPath('http://example.com', 'de')        // => 'http://example.com'
 *
 * @example
 * // 空路径处理
 * getLocalizedPath('', 'uk')                   // => ''
 */
export const getLocalizedPath = (path: string, locale: string): string => {
  if (!path) return path

  // 如果是完整 URL（http:// 或 https://）或锚点链接（#），不处理
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('#')) {
    return path
  }

  // 分割路径并过滤空段（处理多个连续斜杠的情况）
  const pathSegments = path.split('/').filter(Boolean)

  // 如果没有有效路径段，返回 locale 前缀或 '/'
  if (pathSegments.length === 0) {
    return locale === 'us' ? '/' : `/${locale}`
  }

  // 检查路径是否已经包含 locale
  if (pathSegments[0] === locale) {
    return `/${pathSegments.join('/')}`
  }

  // 获取 locale 前缀并拼接
  const localePrefix = locale === 'us' ? '' : `/${locale}`
  return `${localePrefix}/${pathSegments.join('/')}`
}
